import React, { useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { ChatMessage } from '../../types';
import ChatBubble from './ChatBubble';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

interface ChatMessageListProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  streamingContent: string;
  pendingConfirmation: { action: string; summary: string } | null;
  onConfirm: (confirmed: boolean) => void;
}

const TypingIndicator: React.FC = () => (
  <div className="flex justify-start mb-3">
    <div className="bg-gray-100 rounded-lg px-4 py-3">
      <div className="flex space-x-1">
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  </div>
);

const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  isStreaming,
  streamingContent,
  pendingConfirmation,
  onConfirm,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent, isStreaming]);

  if (messages.length === 0 && !isStreaming) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Inizia una conversazione</p>
          <p className="text-xs text-gray-400 mt-1">Chiedimi di creare offerte, cercare clienti e altro</p>
        </div>
      </div>
    );
  }

  // Find last assistant message index to attach confirmation
  let lastAssistantIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant') { lastAssistantIdx = i; break; }
  }

  return (
    <div className="flex-1 overflow-y-auto px-3 py-3">
      {messages.map((msg, idx) => (
        <ChatBubble
          key={msg.id}
          message={msg}
          pendingConfirmation={
            pendingConfirmation && idx === lastAssistantIdx ? pendingConfirmation : null
          }
          onConfirm={onConfirm}
        />
      ))}

      {/* Streaming content */}
      {isStreaming && streamingContent && (
        <div className="flex justify-start mb-3">
          <div className="max-w-[85%] bg-gray-100 rounded-lg rounded-bl-sm px-3 py-2">
            <div className="text-sm prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:my-2 prose-pre:my-2 prose-code:text-xs">
              <ReactMarkdown>{streamingContent}</ReactMarkdown>
            </div>
          </div>
        </div>
      )}

      {/* Typing indicator when streaming but no content yet */}
      {isStreaming && !streamingContent && <TypingIndicator />}

      <div ref={bottomRef} />
    </div>
  );
};

export default ChatMessageList;
